import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback } from 'react';
import { ReturnIcon, useKeyboardNavigation } from '@atlassian/search-dialog';
import SearchIcon from '@atlaskit/icon/core/migration/search';
import FilterIcon from '@atlaskit/icon/core/migration/filter';
import { AdvancedSearchLink, CenteredAdvancedSearchGroup, AdvancedSearchContent } from '../../common/advanced-search-link';
import { useQuery } from '../query-context';
import { addQuery } from '../../utils/url-utils';
import { getTrigger, isNewTab, onAdvancedSearchSelected, useAnalytics } from '../../common/analytics';
import { useIntl } from 'react-intl-next';
import { messages } from '../messages';
import { useProductContext } from '../product-router';
import { ContentWrapper, SearchFooterLabel, SearchFooterLabelWrapper } from './content-wrapper.styled';
import { MultiWorkspaceAdvancedSearchFooter } from '../../common/advanced-search-link';
import { useSessionUserInput } from '../user-input-provider';
import { useDialogExpansionContext } from '../dialog-expansion-context';
import { useSearchSessionId } from '../../common/search-session-provider';
import { ProductKeys } from '../quick-find';
import { CommandIcon } from './icons/command-icon';
import { ControlIcon } from './icons/control-icon';
import { SquareReturnIcon } from './icons/square-return-icon';
var onKeyDown = function onKeyDown(e, target) {
  if (e.key === 'Enter') {
    var _target$firstElementC, _target$firstElementC2;
    e.preventDefault();

    // The ref here is the wrapper span however we simulate a click on the `ResultLinkComponent` that it wraps.
    (_target$firstElementC = target.firstElementChild) === null || _target$firstElementC === void 0 || (_target$firstElementC2 = _target$firstElementC.click) === null || _target$firstElementC2 === void 0 || _target$firstElementC2.call(_target$firstElementC);
  }
};
var onKeyDownWithShift = function onKeyDownWithShift(e, target) {
  if (e.key === 'Enter' && e.shiftKey) {
    var _target$firstElementC3, _target$firstElementC4;
    e.preventDefault();

    // The ref here is the wrapper span however we simulate a click on the `ResultLinkComponent` that it wraps.
    (_target$firstElementC3 = target.firstElementChild) === null || _target$firstElementC3 === void 0 || (_target$firstElementC4 = _target$firstElementC3.click) === null || _target$firstElementC4 === void 0 || _target$firstElementC4.call(_target$firstElementC3);
  }
};
export var ReturnIconWithModifier = function ReturnIconWithModifier(_ref) {
  var showModifierKey = _ref.showModifierKey;
  var ModifierKeyIcon = window.navigator.userAgent.includes('Mac') ? CommandIcon : ControlIcon;
  return /*#__PURE__*/React.createElement(SearchFooterLabelWrapper, null, showModifierKey && /*#__PURE__*/React.createElement(ModifierKeyIcon, null), /*#__PURE__*/React.createElement(SquareReturnIcon, null));
};
export var ReturnKeyIcon = function ReturnKeyIcon(_ref2) {
  var isKeyboardHighlighted = _ref2.isKeyboardHighlighted,
    showSearchOnEnter = _ref2.showSearchOnEnter,
    filterByProductFooter = _ref2.filterByProductFooter,
    squareReturnIcon = _ref2.squareReturnIcon;
  if (showSearchOnEnter) {
    return /*#__PURE__*/React.createElement(SearchFooterLabelWrapper, null, /*#__PURE__*/React.createElement(SearchFooterLabel, null, "Shift"), "+", /*#__PURE__*/React.createElement(ReturnIcon, {
      inverted: isKeyboardHighlighted
    }));
  }
  if (squareReturnIcon) {
    return /*#__PURE__*/React.createElement(ReturnIconWithModifier, {
      showModifierKey: filterByProductFooter
    });
  }
  return /*#__PURE__*/React.createElement(ReturnIcon, {
    inverted: isKeyboardHighlighted
  });
};
export var AdvancedSearchFooterLink = function AdvancedSearchFooterLink(_ref3) {
  var href = _ref3.href,
    _ref3$searchIconLabel = _ref3.searchIconLabel,
    searchIconLabel = _ref3$searchIconLabel === void 0 ? '' : _ref3$searchIconLabel,
    contentLabel = _ref3.contentLabel,
    onClick = _ref3.onClick,
    filterByProductFooter = _ref3.filterByProductFooter,
    showSearchOnEnter = _ref3.showSearchOnEnter,
    squareReturnIcon = _ref3.squareReturnIcon,
    linkComponent = _ref3.linkComponent,
    _ref3$returnKeyIconCo = _ref3.returnKeyIconComponent,
    ReturnKeyIconComponent = _ref3$returnKeyIconCo === void 0 ? ReturnKeyIcon : _ref3$returnKeyIconCo;
  var _useKeyboardNavigatio = useKeyboardNavigation({
      onKeydownCallback: showSearchOnEnter ? onKeyDownWithShift : onKeyDown
    }),
    _useKeyboardNavigatio2 = _slicedToArray(_useKeyboardNavigatio, 2),
    isKeyboardHighlighted = _useKeyboardNavigatio2[0],
    ref = _useKeyboardNavigatio2[1];
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
    React.createElement("span", {
      ref: ref,
      onClick: onClick,
      role: "none"
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      ,
      style: {
        display: 'flex',
        height: '100%',
        width: '100%'
      }
    }, /*#__PURE__*/React.createElement(AdvancedSearchLink, {
      href: href,
      isKeyboardHighlighted: isKeyboardHighlighted,
      linkComponent: linkComponent
    }, /*#__PURE__*/React.createElement(ContentWrapper, null, /*#__PURE__*/React.createElement(CenteredAdvancedSearchGroup, null, filterByProductFooter ? /*#__PURE__*/React.createElement(FilterIcon, {
      color: "currentColor",
      LEGACY_size: "small",
      label: searchIconLabel
    }) : /*#__PURE__*/React.createElement(SearchIcon, {
      color: "currentColor",
      LEGACY_size: "small",
      label: searchIconLabel
    }), /*#__PURE__*/React.createElement(AdvancedSearchContent, null, contentLabel))), /*#__PURE__*/React.createElement(ReturnKeyIconComponent, {
      isKeyboardHighlighted: isKeyboardHighlighted,
      showSearchOnEnter: showSearchOnEnter,
      filterByProductFooter: filterByProductFooter,
      squareReturnIcon: squareReturnIcon
    })))
  );
};
export var AdvancedSearchFooterStateless = function AdvancedSearchFooterStateless(_ref4) {
  var query = _ref4.query,
    isLoading = _ref4.isLoading,
    activeProduct = _ref4.activeProduct,
    closeDialog = _ref4.closeDialog,
    searchFooterLabel = _ref4.searchFooterLabel,
    searchFooterLabelWithIcon = _ref4.searchFooterLabelWithIcon,
    linkComponent = _ref4.linkComponent,
    _ref4$urlGeneratorFor = _ref4.urlGeneratorForNoResultsScreen,
    urlGeneratorForNoResultsScreen = _ref4$urlGeneratorFor === void 0 ? function () {
      return '';
    } : _ref4$urlGeneratorFor,
    _ref4$squareReturnIco = _ref4.squareReturnIcon,
    squareReturnIcon = _ref4$squareReturnIco === void 0 ? false : _ref4$squareReturnIco,
    _ref4$filterByProduct = _ref4.filterByProductFooter,
    filterByProductFooter = _ref4$filterByProduct === void 0 ? false : _ref4$filterByProduct,
    returnKeyIconComponent = _ref4.returnKeyIconComponent;
  var intl = useIntl();
  var searchSessionId = useSearchSessionId();
  var productName = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.title;
  var productId = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.id;
  var urlGenerator = (activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.generateAdvancedSearchUrl) || urlGeneratorForNoResultsScreen;
  var baseSearchUrl = filterByProductFooter ? "".concat(urlGenerator(query), "&product=confluence") : urlGenerator(query);
  var advancedSearchUrl = query && productId === ProductKeys.Confluence ? addQuery(baseSearchUrl, 'search_id', searchSessionId) : baseSearchUrl;
  var workspaces = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.workspaces;
  var showSearchOnEnter = !!(activeProduct !== null && activeProduct !== void 0 && activeProduct.showSearchOnEnter);
  var actionSubjectId = "".concat(productName, "AdvancedSearchLink");
  var _useSessionUserInput = useSessionUserInput(),
    resetSearchSession = _useSessionUserInput.resetSearchSession;
  var searchLabel = searchFooterLabel || intl.formatMessage(messages.extensible_advanced_search_footer);
  var advancedSearchContentLabel = searchFooterLabelWithIcon || searchLabel;
  var _useAnalytics = useAnalytics(),
    fireAnalyticsEvent = _useAnalytics.fireAnalyticsEvent;
  var onAdvancedSearchClicked = useCallback(function (e) {
    resetSearchSession();
    if (filterByProductFooter) {
      closeDialog();
    }
    fireAnalyticsEvent(onAdvancedSearchSelected({
      trigger: getTrigger(e),
      actionSubjectId: actionSubjectId,
      isLoading: isLoading,
      newTab: isNewTab(e)
    }));
  }, [fireAnalyticsEvent, isLoading, actionSubjectId, resetSearchSession, closeDialog, filterByProductFooter]);
  if (workspaces && (workspaces === null || workspaces === void 0 ? void 0 : workspaces.length) > 1) {
    var workspacesWithAdvancedSearchLinks = workspaces.map(function (workspace) {
      var _activeProduct$genera;
      return {
        product: productName || '',
        content: workspace.name,
        key: workspace.name,
        advancedSearchUrl: (activeProduct === null || activeProduct === void 0 || (_activeProduct$genera = activeProduct.generateAdvancedSearchUrl) === null || _activeProduct$genera === void 0 ? void 0 : _activeProduct$genera.call(activeProduct, query, workspace.baseUrl)) || ''
      };
    });
    return /*#__PURE__*/React.createElement(MultiWorkspaceAdvancedSearchFooter, {
      workspaces: workspacesWithAdvancedSearchLinks,
      linkComponent: linkComponent,
      isLoading: isLoading,
      onClick: function onClick(_, e) {
        onAdvancedSearchSelected({
          actionSubjectId: actionSubjectId,
          trigger: getTrigger(e),
          isLoading: isLoading,
          newTab: isNewTab(e)
        });
      }
    });
  }
  return /*#__PURE__*/React.createElement(AdvancedSearchFooterLink, {
    href: advancedSearchUrl,
    searchIconLabel: searchLabel,
    contentLabel: advancedSearchContentLabel,
    onClick: onAdvancedSearchClicked,
    filterByProductFooter: filterByProductFooter,
    showSearchOnEnter: showSearchOnEnter,
    squareReturnIcon: squareReturnIcon,
    linkComponent: linkComponent,
    returnKeyIconComponent: returnKeyIconComponent
  });
};
export var AdvancedSearchFooter = function AdvancedSearchFooter(_ref5) {
  var rest = _extends({}, (_objectDestructuringEmpty(_ref5), _ref5));
  var _useQuery = useQuery(),
    query = _useQuery.query,
    isLoading = _useQuery.isLoading;
  var _useDialogExpansionCo = useDialogExpansionContext(),
    setIsExpanded = _useDialogExpansionCo.setIsExpanded;
  var _useProductContext = useProductContext(),
    getActiveProduct = _useProductContext.getActiveProduct;
  var activeProduct = getActiveProduct();
  var closeDialog = useCallback(function () {
    setIsExpanded(false);
  }, [setIsExpanded]);
  return /*#__PURE__*/React.createElement(AdvancedSearchFooterStateless, _extends({}, rest, {
    query: query,
    isLoading: isLoading,
    activeProduct: activeProduct,
    closeDialog: closeDialog
  }));
};